/**
 * ChangeTypeViewDropdown - View switcher for the Control Chart (Change Log)
 *
 * Mirrors the UX of the Mechanical tracker's table dropdown: lets the user
 * view the change log filtered to a single change type (Lump Sum, T&M,
 * Tracking Only, Material Supply Only, Unspecified) or "All Change Types".
 *
 * Options, counts and colors are passed in by the parent so the dropdown
 * stays a dumb presentational component.
 */

import React, { useState, useRef, useEffect } from 'react';

/**
 * @param {string}   value     - currently selected option id
 * @param {function} onChange  - callback(optionId) when a new option is picked
 * @param {Array}    options   - [{ id, label, count, color }]
 * @param {string}   label     - leading label (default "View:")
 */
const ChangeTypeViewDropdown = ({ value, onChange, options = [], label = 'View:' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const current = options.find(o => o.id === value) || options[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (optionId) => {
    if (optionId !== value) onChange(optionId);
    setIsOpen(false);
  };

  const buttonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
    color: '#212529',
    minWidth: '210px',
    justifyContent: 'space-between',
    transition: 'all 0.2s ease',
  };

  const menuStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    backgroundColor: '#fff',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    overflow: 'hidden',
  };

  const menuItemStyle = (isSelected) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '10px 12px',
    cursor: 'pointer',
    backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
    borderLeft: isSelected ? '3px solid #1976d2' : '3px solid transparent',
    fontSize: '0.9rem',
    color: isSelected ? '#1565c0' : '#212529',
    fontWeight: isSelected ? '600' : '400',
    transition: 'all 0.15s ease',
  });

  const Dot = ({ color }) => (
    <span style={{
      display: 'inline-block',
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      backgroundColor: color || '#9ca3af',
      flexShrink: 0,
    }} />
  );

  const CountBadge = ({ count, active }) => (
    <span style={{
      fontSize: '0.75rem',
      fontWeight: '600',
      color: active ? '#1565c0' : '#6b7280',
      backgroundColor: active ? '#dbeafe' : '#f3f4f6',
      borderRadius: '10px',
      padding: '1px 8px',
      minWidth: '20px',
      textAlign: 'center',
    }}>{count}</span>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ fontSize: '0.8rem', color: '#6c757d', whiteSpace: 'nowrap' }}>{label}</span>

      <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          style={buttonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#80bdff';
            e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0, 123, 255, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#d1d5db';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {current && <Dot color={current.color} />}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{current ? current.label : 'All'}</span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {current && <CountBadge count={current.count} active />}
            <svg
              width="12" height="12" viewBox="0 0 24 24" fill="currentColor"
              style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
            >
              <path d="M7 10l5 5 5-5z" />
            </svg>
          </span>
        </button>

        {isOpen && (
          <div style={menuStyle}>
            {options.map((opt) => {
              const isSelected = opt.id === value;
              return (
                <div
                  key={opt.id}
                  onClick={() => handleSelect(opt.id)}
                  style={menuItemStyle(isSelected)}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Dot color={opt.color} />
                    {opt.label}
                  </span>
                  <CountBadge count={opt.count} active={isSelected} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangeTypeViewDropdown;
